import type { App, ConcreteComponent } from 'vue';
import { FormControllerComponent } from './Components/Controllers/FormControllerComponent.ts';
import { RouterViewComponent } from './Components/Routing/RouterViewComponent.ts';
import { RouterLinkComponent } from './Components/Routing/RouterLinkComponent.ts';
import { RouterNestedComponent } from './Components/Routing/RouterNestedComponent.ts';
import { RouterFrameComponent } from './Components/Routing/RouterFrameComponent.ts';
import { ToastControllerComponent, ToastComponent } from './Components/Controllers/ToastControllerComponent.ts';
import { PasswordConfirmationControllerComponent } from './Components/Controllers/PasswordConfirmationControllerComponent.ts';
import { StackedViewLayoutInjectionKey } from './Composables/UseStackLayout.ts';
import type { Shared } from './Types/State.ts';
import { UrlGenerator, UrlGeneratorInjectionKey } from './Services/UrlGenerator.ts';
import { Router, RouterInjectionKey } from './Services/RouteGenerator.ts';
import { Translator, TranslatorInjectionKey } from './Services/Translator.ts';
import { CountriesManager, CountriesManagerInjectionKey } from './Services/CountriesManager.ts';

export * from './Application/Factory.ts';

export * from './Components/Controllers/FormControllerComponent.ts';
export * from './Components/Controllers/ToastControllerComponent.ts';

export * from './Components/Routing/RouterComponent.ts';
export * from './Components/Routing/RouterLinkComponent.ts';
export * from './Components/Routing/RouterViewComponent.ts';
export * from './Components/Routing/RouterNestedComponent.ts';
export * from './Components/Routing/RouterFrameComponent.ts';

export * from './Composables/UseConfirmation.ts';
export * from './Composables/UseDialogStack.ts';
export * from './Composables/UseStackLayout.ts';
export * from './Composables/UseFormApi.ts';
export * from './Composables/UseFormContext.ts';
export * from './Composables/UseHttpClient.ts';
export * from './Composables/UseViewDepth.ts';
export * from './Composables/UseViewLocation.ts';
export * from './Composables/UseViewParent.ts';
export * from './Composables/UseViewQuery.ts';
export * from './Composables/UseViewResolver.ts';
export * from './Composables/UseViewStack.ts';

export * from './Events/EventBus.ts';

export * from './Http/Client/Request.ts';
export * from './Http/Client/Response.ts';

export * from './Services/CountriesManager.ts';
export * from './Services/FormContext.ts';
export * from './Services/RouteGenerator.ts';
export * from './Services/StateManager.ts';
export * from './Services/StackedView.ts';
export * from './Services/ToastManager.ts';
export * from './Services/Translator.ts';
export * from './Services/UrlGenerator.ts';

export * from './Support/Array.ts';
export * from './Support/Empty.ts';
export * from './Support/ErrorModal.ts';
export * from './Support/FormData.ts';
export * from './Support/Hash.ts';
export * from './Support/NestedSet.ts';
export * from './Support/Random.ts';
export * from './Support/Scroll.ts';
export * from './Support/Wrap.ts';

export * from './Types/HeadMeta.ts';
export * from './Types/RouterRedirect.ts';
export * from './Types/StackedView.ts';
export * from './Types/State.ts';
export * from './Types/Toast.ts';
export * from './Types/ViewResolver.ts';

interface Configuration {
  shared: Shared,
  layout?: ConcreteComponent | string | undefined,
}

export function createOtherSoftwareFoundation(options: Configuration) {
  return {
    install(app: App) {
      app.component('RouterFrame', RouterFrameComponent);
      app.component('RouterNested', RouterNestedComponent);
      app.component('RouterView', RouterViewComponent);
      app.component('RouterLink', RouterLinkComponent);
      app.component('FormController', FormControllerComponent);
      app.component('PasswordConfirmationController', PasswordConfirmationControllerComponent);
      app.component('ToastController', ToastControllerComponent);
      app.component('Toast', ToastComponent);

      const urlGenerator = new UrlGenerator(options.shared.appUrl);

      const routeGenerator = new Router(urlGenerator, {
        locale: options.shared.appLocale,
        fallback: options.shared.appFallbackLocale,
        available: options.shared.appAvailableLocales,
        routes: options.shared.appRoutes,
      });

      const translator = new Translator(options.shared.appLocale, options.shared.appTranslations);
      const countries = new CountriesManager(options.shared.appCountriesImplicitlyAddressed);

      app.provide(StackedViewLayoutInjectionKey, options.layout);
      app.provide(UrlGeneratorInjectionKey, urlGenerator);
      app.provide(RouterInjectionKey, routeGenerator);
      app.provide(TranslatorInjectionKey, translator);
      app.provide(CountriesManagerInjectionKey, countries);

      app.config.globalProperties.$t = translator.trans.bind(translator);
      app.config.globalProperties.$tc = translator.choice.bind(translator);
      app.config.globalProperties.$url = urlGenerator.generate.bind(urlGenerator);
      app.config.globalProperties.$route = routeGenerator.route.bind(routeGenerator);
      app.config.globalProperties.$isImplicitCountry = countries.isImplicit.bind(countries);
      app.config.globalProperties.$isExplicitCountry = countries.isExplicit.bind(countries);
    },
  };
}
